/**
 * Sentry Error Tracking Configuration
 *
 * This module provides comprehensive error tracking for the EdConnex application.
 * It captures JavaScript errors, API failures, performance metrics, and user interactions.
 */

import * as Sentry from "@sentry/react";
import { config, environment } from "../../config";

// Sentry configuration options
interface SentryConfig {
  dsn: string;
  environment: string;
  enabled: boolean;
  debug: boolean;
  sampleRate: number;
  tracesSampleRate: number;
  replaysSessionSampleRate: number;
  replaysOnErrorSampleRate: number;
}

// Get Sentry configuration based on environment
const getSentryConfig = (): SentryConfig => {
  const dsn = import.meta.env.VITE_SENTRY_DSN || "";
  const isEnabled = import.meta.env.VITE_SENTRY_ENABLED !== "false" && !!dsn;

  return {
    dsn,
    environment,
    enabled: isEnabled,
    debug: config.isDevelopment() && config.enableDebug,
    // Error sample rate (1.0 = 100% of errors captured)
    sampleRate: config.isProduction() ? 1.0 : 1.0,
    // Performance tracing sample rate
    tracesSampleRate: config.isProduction() ? 0.2 : 0.5,
    // Session replay sample rate (only in production to save quota)
    replaysSessionSampleRate: config.isProduction() ? 0.1 : 0,
    replaysOnErrorSampleRate: config.isProduction() ? 1.0 : 0.5,
  };
};

// Patterns to ignore (reduce noise)
const IGNORED_ERRORS = [
  // Browser extensions
  /extensions\//i,
  /^chrome:\/\//i,
  /^chrome-extension:\/\//i,
  /^moz-extension:\/\//i,

  // Network errors that are expected
  "Network request failed",
  "Failed to fetch",
  "Load failed",
  "NetworkError",
  "AbortError",

  // User-initiated cancellations
  "Request aborted",
  "cancelled",

  // Irrelevant browser errors
  "ResizeObserver loop",
  "Non-Error promise rejection",
];

// URLs to ignore
const IGNORED_URLS = [
  // Third-party scripts
  /google-analytics\.com/i,
  /googletagmanager\.com/i,
  /facebook\.com/i,
  /clarity\.ms/i,
];

/**
 * Initialize Sentry error tracking
 * Call this at the very start of your application (before rendering)
 */
export const initSentry = (): void => {
  const sentryConfig = getSentryConfig();

  if (!sentryConfig.enabled) {
    console.log("Sentry is disabled (no DSN or explicitly disabled)");
    return;
  }

  try {
    Sentry.init({
      dsn: sentryConfig.dsn,
      environment: sentryConfig.environment,
      debug: sentryConfig.debug,

      // Release tracking (set during build)
      release: import.meta.env.VITE_SENTRY_RELEASE || `edconnex@${environment}`,

      // Sample rates
      sampleRate: sentryConfig.sampleRate,
      tracesSampleRate: sentryConfig.tracesSampleRate,
      replaysSessionSampleRate: sentryConfig.replaysSessionSampleRate,
      replaysOnErrorSampleRate: sentryConfig.replaysOnErrorSampleRate,

      // Integrations
      integrations: [
        // Browser tracing for performance monitoring
        Sentry.browserTracingIntegration({
          // Track navigation and page load
          enableInp: true,
        }),

        // Session replay for debugging (only in production)
        ...(config.isProduction()
          ? [
              Sentry.replayIntegration({
                // Mask all text content for privacy
                maskAllText: true,
                // Block all media for privacy
                blockAllMedia: true,
              }),
            ]
          : []),

        // Capture console errors only (not info/debug logs)
        Sentry.captureConsoleIntegration({
          levels: ["error"],
        }),

        // HTTP client tracking
        Sentry.httpClientIntegration(),

        // Breadcrumbs for debugging
        Sentry.breadcrumbsIntegration({
          console: true,
          dom: true,
          fetch: true,
          history: true,
          xhr: true,
        }),
      ],

      // Filter out noisy errors
      ignoreErrors: IGNORED_ERRORS,
      denyUrls: IGNORED_URLS,

      // Before sending event - sanitize and filter
      beforeSend(event, hint) {
        const error = hint.originalException;

        // Filter out specific error types
        if (error instanceof Error) {
          // Ignore network errors during offline state
          if (
            !navigator.onLine &&
            (error.message.includes("fetch") ||
              error.message.includes("network"))
          ) {
            return null;
          }

          // Ignore cancelled requests
          if (
            error.name === "AbortError" ||
            error.message.includes("cancelled")
          ) {
            return null;
          }
        }

        // Sanitize sensitive data
        if (event.request) {
          // Remove authorization headers
          if (event.request.headers) {
            delete event.request.headers["Authorization"];
            delete event.request.headers["authorization"];
          }

          // Remove sensitive query params (handle both string and array formats)
          if (event.request.query_string) {
            if (typeof event.request.query_string === "string") {
              event.request.query_string = event.request.query_string
                .replace(/password=[^&]*/gi, "password=[REDACTED]")
                .replace(/token=[^&]*/gi, "token=[REDACTED]")
                .replace(/key=[^&]*/gi, "key=[REDACTED]");
            }
          }
        }

        // Remove PII from user data if present
        if (event.user) {
          // Keep useful identifiers but remove sensitive info
          delete event.user.ip_address;
        }

        return event;
      },

      // Before sending breadcrumbs - filter sensitive data
      beforeBreadcrumb(breadcrumb) {
        // Filter out sensitive API calls from breadcrumbs
        if (breadcrumb.category === "fetch" || breadcrumb.category === "xhr") {
          const url = breadcrumb.data?.url || "";
          if (
            url.includes("/login") ||
            url.includes("/password") ||
            url.includes("/token")
          ) {
            // Keep the breadcrumb but remove sensitive data
            if (breadcrumb.data) {
              delete breadcrumb.data.request_body;
              delete breadcrumb.data.response_body;
            }
          }
        }

        return breadcrumb;
      },

      // Attach stack trace to all messages
      attachStacktrace: true,

      // Maximum breadcrumbs to keep
      maxBreadcrumbs: 50,

      // Enable sending default PII (controlled, we sanitize above)
      sendDefaultPii: false,

      // Normalize stack traces depth
      normalizeDepth: 5,
    });

    // Set initial tags
    Sentry.setTag("app.version", import.meta.env.VITE_APP_VERSION || "unknown");
    Sentry.setTag("app.environment", environment);

    console.log(`Sentry initialized for ${environment} environment`);
  } catch (error) {
    console.error("Failed to initialize Sentry:", error);
  }
};

/**
 * Set user context for better error tracking
 */
export const setSentryUser = (user: {
  id?: string;
  email?: string;
  role?: string;
}) => {
  if (!user) {
    Sentry.setUser(null);
    return;
  }

  Sentry.setUser({
    id: user.id,
    // Don't send email in production for privacy
    email: config.isProduction() ? undefined : user.email,
  });

  // Set role as a tag for filtering
  if (user.role) {
    Sentry.setTag("user.role", user.role);
  }
};

/**
 * Clear user context (on logout)
 */
export const clearSentryUser = () => {
  Sentry.setUser(null);
};

/**
 * Capture an exception with additional context
 */
export const captureException = (
  error: Error | unknown,
  context?: {
    tags?: Record<string, string>;
    extra?: Record<string, unknown>;
    level?: Sentry.SeverityLevel;
  }
) => {
  Sentry.withScope((scope) => {
    if (context?.tags) {
      Object.entries(context.tags).forEach(([key, value]) => {
        scope.setTag(key, value);
      });
    }

    if (context?.extra) {
      Object.entries(context.extra).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });
    }

    if (context?.level) {
      scope.setLevel(context.level);
    }

    Sentry.captureException(error);
  });
};

/**
 * Capture a message with context
 */
export const captureMessage = (
  message: string,
  level: Sentry.SeverityLevel = "info",
  context?: Record<string, unknown>
) => {
  Sentry.withScope((scope) => {
    scope.setLevel(level);

    if (context) {
      Object.entries(context).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });
    }

    Sentry.captureMessage(message);
  });
};

/**
 * Add breadcrumb for debugging
 */
export const addBreadcrumb = (breadcrumb: {
  category: string;
  message: string;
  level?: Sentry.SeverityLevel;
  data?: Record<string, unknown>;
}) => {
  Sentry.addBreadcrumb({
    category: breadcrumb.category,
    message: breadcrumb.message,
    level: breadcrumb.level || "info",
    data: breadcrumb.data,
    timestamp: Date.now() / 1000,
  });
};

/**
 * Capture API error with structured context
 */
export const captureApiError = (
  error: unknown,
  endpoint: string,
  method: string,
  statusCode?: number
) => {
  captureException(error, {
    tags: {
      "api.endpoint": endpoint,
      "api.method": method,
      "api.status_code": statusCode?.toString() || "unknown",
    },
    extra: {
      url: endpoint,
      method,
      statusCode,
    },
    level: statusCode && statusCode >= 500 ? "error" : "warning",
  });
};

/**
 * Start a performance transaction
 */
export const startTransaction = (name: string, op: string) => {
  return Sentry.startInactiveSpan({
    name,
    op,
  });
};

// Export Sentry for direct access when needed
export { Sentry };

// Export error boundary for wrapping components
export const SentryErrorBoundary = Sentry.ErrorBoundary;

// Export profiler for component profiling
export const SentryProfiler = Sentry.withProfiler;
